/* eslint-disable no-nested-ternary */
import ConversationsService from '../services/conversations/conversations.service';
import { AdminOverviewQueryPayload } from '../services/customer/customer.service';

// TODO: Unify fetchOverviewCustomers to get list in my conversations and all conversations
export async function fetchOverviewCustomers(
  workspaceId: number,
  payload: AdminOverviewQueryPayload,
  setCustomerList: Function,
  setCustomers: Function,
  fetchingOverviewData: Function,
  handleShowSnackbar: Function,
  homePageListErrorEvent: Function,
) {
  fetchingOverviewData(true);
  try {
    const response = await ConversationsService.getMyConversationsOverview(
      workspaceId,
      payload,
    );
    const { data } = response?.data;
    const { data: list } = data;
    setCustomerList(list);
    setCustomers(list.map((x: any) => x.cust_id));
  } catch (err: any) {
    fetchingOverviewData(false);
    homePageListErrorEvent(err.response?.data.message);
    handleShowSnackbar('error', err.response?.data.message);
    return;
  } finally {
    fetchingOverviewData(false);
  }
}

export function generateOverviewPayload({
  query,
  overviewParams,
  checkedMeetingSource,
  checkedFilteredStatus,
  checkedFilteredIntent,
  checkedFilteredAssignee,
  selectedQualificationQuestions,
  selectedAdditionalCRMFields,
  checkedLabels,
  searchDate,
  callQualityScore,
  opportunityLabelsList,
  isAdmin,
  isManager,
  user,

  // Helper functions
  getSearchDateFromURL,
  getQualificationQuestionFormat,
  updateMeetingSource,
  updateAssigneeConveration,
  updateIntentConversation,
  updateStatusConversation,
  updateStatusConversationIds,
}: {
  query: Record<string, any>;
  overviewParams: {
    params: {
      page_number?: number;
      sort_field?: string;
      sort?: string;
      assigned_intent?: string;
      assigned_status?: string;
    };
  };
  checkedMeetingSource: any[];
  checkedFilteredStatus: any[];
  checkedFilteredIntent: any[];
  checkedFilteredAssignee: any[];
  selectedQualificationQuestions?: any[];
  selectedAdditionalCRMFields: Record<string, any>;
  checkedLabels: any[];
  searchDate: string;
  callQualityScore: { min: number; max: number };
  opportunityLabelsList: any[];
  isAdmin: boolean;
  isManager: boolean;
  user: { id: number; email?: string };

  // Helper functions
  getSearchDateFromURL: () => string;
  getQualificationQuestionFormat?: (arr: any[]) => any[];
  updateMeetingSource: (arr: any[]) => number[];
  updateAssigneeConveration: (arr: any[]) => number[];
  updateIntentConversation: (arr: any[]) => string[];
  updateStatusConversation: (arr: any[]) => string[];
  updateStatusConversationIds: (arr: any[]) => number[];
}) {
  // Local storage checks from your existing code
  const getSessionItem = (key: string, fallback: any) =>
    typeof window !== 'undefined' && window.sessionStorage.getItem(key)
      ? JSON.parse(window.sessionStorage.getItem(key) as string)
      : fallback;

  const existingSource = getSessionItem('meeting_source', []);
  const existingStatus = getSessionItem('filtered_status', []);
  const existingIntent = getSessionItem('filtered_intent', []);
  const existingAssignee = getSessionItem('filtered_assignee', []);
  const existingDates = getSessionItem('filtered_date', '');
  const existingScore = getSessionItem('quality_score', {});
  const existingAdditionalCRMFields = getSessionItem(
    'filtered_additional_crm_fields',
    {},
  );
  const existingLabels = getSessionItem('opportunity_type_ids', []);

  const meetingSource = checkedMeetingSource?.length
    ? checkedMeetingSource
    : existingSource;
  const crmFilterStatus = checkedFilteredStatus?.length
    ? checkedFilteredStatus
    : existingStatus;
  const filteredIntent = checkedFilteredIntent?.length
    ? checkedFilteredIntent
    : existingIntent;
  const filteredAssignee = checkedFilteredAssignee?.length
    ? checkedFilteredAssignee
    : existingAssignee;
  const filterScore = Object.keys(existingScore).length
    ? existingScore
    : callQualityScore;
  const filteredLabels = checkedLabels?.length ? checkedLabels : existingLabels;
  const filterAdditionalCRMFields = Object.keys(existingAdditionalCRMFields)
    .length
    ? existingAdditionalCRMFields
    : selectedAdditionalCRMFields || {};

  // Determine final date string
  let filterDates = searchDate?.length
    ? searchDate
    : existingDates
    ? JSON.stringify(existingDates)
    : '';
  // If query.search_param is present, we use getSearchDateFromURL
  if (query?.search_param) {
    filterDates = getSearchDateFromURL();
  }

  const searchDateRange = filterDates ? JSON.parse(filterDates) : [];
  const searchObject = {
    ...(searchDateRange?.length && searchDateRange[0]
      ? { start_date: searchDateRange[0] }
      : {}),
    ...(searchDateRange?.length && searchDateRange[1]
      ? { end_date: searchDateRange[1] }
      : {}),
  };

  const meetingSourceQ = updateMeetingSource(meetingSource)?.toString() || null;
  const assignedUserIdQ =
    updateAssigneeConveration(filteredAssignee)?.toString() || null;
  const assignedIntentQ =
    updateIntentConversation(filteredIntent)?.toString() || null;
  const assignedStatusQ =
    updateStatusConversation(crmFilterStatus)?.toString() || null;

  const rawAssignedIntent = overviewParams.params.assigned_intent;
  const rawAssignedStatus = overviewParams.params.assigned_status;

  // Get array of intents from overviewParams
  const assignedIntentFromOverview = rawAssignedIntent
    ? [rawAssignedIntent]
    : [];

  // Split and clean comma-separated status string
  const assignedStatusNamesFromOverview = rawAssignedStatus
    ? [...new Set(rawAssignedStatus.split(',').map(name => name.trim()))]
    : [];

  // Match names to IDs from checkedFilteredStatus
  const matchedStatusIdsFromOverview = checkedFilteredStatus
    .filter(status => assignedStatusNamesFromOverview.includes(status.name))
    .map(status => status.id);

  let filteredQuestions: any[] = [];
  if (getQualificationQuestionFormat && selectedQualificationQuestions) {
    filteredQuestions = getQualificationQuestionFormat(
      selectedQualificationQuestions,
    );
  }

  const pageNumber =
    Number(query?.page_number) || overviewParams.params.page_number || 1;
  const field = (query?.sort_field || 'last_conversation_at').toString();
  const order: 'ASC' | 'DESC' =
    (query?.sort || 'desc').toString().toUpperCase() === 'ASC' ? 'ASC' : 'DESC';

  const params = {
    page_number: pageNumber,
    sort_field: field,
    sort: order === 'ASC' ? 'asc' : 'desc',
    // Example of passing search_term/customer_id if present
    ...(query?.search_term ? { search_term: query?.search_term } : {}),
    ...(query?.customer_id ? { customer_id: query?.customer_id } : {}),
    // if your original snippet had search_date or last_conversation_date
    ...(query?.search_date ? { search_date: query?.search_date } : {}),
    ...(filterDates ? { last_conversation_date: filterDates } : {}),

    // "Q" versions
    ...(meetingSourceQ ? { meeting_source_id: meetingSourceQ } : {}),
    ...(assignedUserIdQ ? { assigned_user_id: assignedUserIdQ } : {}),
    ...(assignedIntentQ ? { assigned_intent: assignedIntentQ } : {}),
    ...(assignedStatusQ ? { assigned_status: assignedStatusQ } : {}),

    // If you have a manager filter
    ...(!isAdmin && isManager ? { manager_id: user.id } : {}),

    // Quality score
    ...(filterScore?.min ? { quality_score_min: filterScore.min } : {}),
    ...(filterScore?.max ? { quality_score_max: filterScore.max } : {}),

    // Opportunity labels
    ...(filteredLabels?.length
      ? { opportunity_type_ids: filteredLabels.join(',') }
      : {}),

    // Additional CRM fields
    ...(filterAdditionalCRMFields
      ? { crm_additional_fields: filterAdditionalCRMFields }
      : {}),

    // Qualification questions
    ...(filteredQuestions?.length
      ? { qualification_questions: filteredQuestions }
      : {}),

    // If you want to pass the raw status array
    crmFilterStatus,
  };

  // -----------------------------
  // 2) Build the "payload" object
  // -----------------------------
  const payload = {
    page: Number(pageNumber),
    sort: [{ field, order }],
    filters: {
      // Query-based
      ...(query?.search_term ? { customer_name: query?.search_term } : {}),
      ...(query?.customer_id ? { customer_id: query?.customer_id } : {}),
      ...(query?.search_date ? { search_date: query?.search_date } : {}),
      ...(filteredAssignee.length
        ? { assigned_user_ids: updateAssigneeConveration(filteredAssignee) }
        : {}),
      ...(assignedIntentFromOverview.length
        ? { intents: assignedIntentFromOverview }
        : assignedIntentQ
        ? { intents: assignedIntentQ.split(',') }
        : {}),
      ...(matchedStatusIdsFromOverview.length
        ? { crm_status_ids: matchedStatusIdsFromOverview }
        : assignedStatusQ
        ? { crm_status_ids: updateStatusConversationIds(crmFilterStatus) }
        : {}),
      ...(filterScore?.min || filterScore?.max
        ? { audit_score_range: { min: filterScore.min, max: filterScore.max } }
        : {}),
      ...(meetingSourceQ
        ? { meeting_source_ids: updateMeetingSource(meetingSource) }
        : {}),
      ...(!isAdmin && isManager ? { manager_id: user.id } : {}),
      ...(Object.keys(searchObject).length
        ? { search_date: searchObject }
        : {}),

      // Opportunity labels
      ...(filteredLabels.length &&
      filteredLabels.length !== opportunityLabelsList.length
        ? { opportunity_type_ids: filteredLabels }
        : {}),

      // Additional CRM fields
      ...(Object.keys(filterAdditionalCRMFields).length
        ? { crm_additional_fields: filterAdditionalCRMFields }
        : {}),

      // Qualification questions
      ...(filteredQuestions.length
        ? { qualification_questions: filteredQuestions }
        : {}),
    },
  };

  if(payload?.filters?.assigned_user_ids?.length === 0) delete payload?.filters?.assigned_user_ids;

  return { params, payload };
}
